import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { SiteBadge, variance } from "./CheckInPanel";
import { money } from "@/components/shared";

const SELECT = "w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand";
const t = (iso) => (iso ? format(parseISO(iso), "h:mma") : "—");
const today = new Date();
const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
const iso = (d) => format(d, "yyyy-MM-dd");

export default function Timesheets() {
  const [team, setTeam] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [range, setRange] = useState({ start: iso(monday), end: iso(today) });
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!range.start || !range.end) return;
    api.get("/reports/timesheets", { params: { ...range, staff_id: staffId } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range, staffId]);

  const csv = async () => {
    try {
      const res = await api.get("/reports/timesheets", { params: { ...range, staff_id: staffId, format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheets-${range.start}-to-${range.end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export timesheets"); }
  };

  return (
    <div data-testid="timesheets">
      <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
        <div className="space-y-2 md:col-span-2">
          <Label className="label-caps text-muted-foreground">Cleaner</Label>
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={SELECT} data-testid="timesheet-staff-select">
            <option value="">All cleaners</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="space-y-2"><Label className="label-caps text-muted-foreground">From</Label>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="timesheet-start" /></div>
        <div className="space-y-2"><Label className="label-caps text-muted-foreground">To</Label>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="timesheet-end" /></div>
      </section>

      {data && (
        <section className="mt-8 border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="timesheet-results">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-4" data-testid="timesheet-totals">
              {data.totals.length === 0 && <span className="text-sm text-muted-foreground">No started or completed jobs in this period.</span>}
              {data.totals.map((a) => {
                const v = Math.round((a.actual_hours - a.planned_hours) * 100) / 100;
                return (
                  <div key={a.staff_id} className="rounded-sm bg-[hsl(330_60%_97%)] border border-[hsl(330_40%_92%)] px-5 py-3" data-testid={`timesheet-total-${a.staff_id}`}>
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{a.jobs} jobs · planned {a.planned_hours} h · actual {a.actual_hours} h · <span className={v > 0 ? "text-destructive" : "text-[hsl(140_60%_32%)]"}>{variance(v)}</span></div>
                    <div className="text-xs mt-1" data-testid={`timesheet-pay-${a.staff_id}`}>Pay <strong>{money(a.pay)}</strong> <span className="text-muted-foreground">@ {money(a.hourly_rate)}/h</span></div>
                  </div>
                );
              })}
            </div>
            <Button variant="outline" onClick={csv} data-testid="timesheet-download-csv" className="rounded-sm gap-2"><FileSpreadsheet className="w-4 h-4" /> Export CSV</Button>
          </div>
          {data.rows.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Cleaner(s)</TableHead><TableHead>Client</TableHead>
                  <TableHead>Planned</TableHead><TableHead>Actual</TableHead><TableHead>Variance</TableHead><TableHead>On site</TableHead><TableHead className="text-right">Pay</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((r) => (
                    <TableRow key={r.job_id} data-testid={`timesheet-row-${r.job_id}`}>
                      <TableCell className="whitespace-nowrap">{r.planned_start ? format(parseISO(r.planned_start), "EEE d MMM") : "—"}</TableCell>
                      <TableCell>{r.cleaners || "—"}</TableCell>
                      <TableCell><Link to={`/jobs?job=${r.job_id}`} className="text-brand hover:underline">{r.client}</Link></TableCell>
                      <TableCell className="whitespace-nowrap">{t(r.planned_start)}–{t(r.planned_end)} · {r.planned_hours} h</TableCell>
                      <TableCell className="whitespace-nowrap">{t(r.actual_start)}–{t(r.actual_end)} · {r.actual_hours ?? "—"} h{r.edited && <span className="ml-1 text-[10px] uppercase text-muted-foreground">(edited)</span>}</TableCell>
                      <TableCell className={r.variance_hours > 0 ? "text-destructive" : r.variance_hours < 0 ? "text-[hsl(140_60%_32%)]" : ""}>{variance(r.variance_hours)}</TableCell>
                      <TableCell><div className="flex flex-col gap-0.5"><SiteBadge loc={r.start_location} label="Start" /><SiteBadge loc={r.end_location} label="Finish" /></div></TableCell>
                      <TableCell className="text-right whitespace-nowrap" data-testid={`timesheet-row-pay-${r.job_id}`}>{money(r.pay)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
